import React, { useState, useEffect } from 'react';
import { adminService } from '../services/authService';

interface Dream {
  id: string;
  title: string;
  content: string;
  userId: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  interpretations: any[];
  symbols: any[];
}

interface DreamsResponse {
  dreams: Dream[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const DreamsPage: React.FC = () => {
  const [dreams, setDreams] = useState<Dream[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDream, setSelectedDream] = useState<Dream | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    fetchDreams();
  }, [pagination.page, searchTerm]);

  const fetchDreams = async () => {
    try {
      setLoading(true);
      const data: DreamsResponse = await adminService.getDreams(
        pagination.page,
        pagination.limit,
        searchTerm
      );
      setDreams(data.dreams);
      setPagination(data.pagination);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dreams:', error);
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Rüyalar</h1>
        <p className="mt-1 text-sm text-gray-500">
          Kullanıcıların paylaştığı rüyaları görüntüleyin ve yönetin
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="max-w-md">
          <input
            type="text"
            placeholder="Rüya ara..."
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>
      </div>

      {/* Dreams List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {dreams.map((dream) => (
            <li key={dream.id}>
              <div className="px-4 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">
                        {dream.title}
                      </h3>
                      <div className="flex space-x-2">
                        {dream.interpretations && dream.interpretations.length > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Yorumlanmış
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Beklemede
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        {truncateContent(dream.content)}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <span>{dream.user.name} ({dream.user.email})</span>
                      <span className="mx-2">•</span>
                      <span>{formatDate(dream.createdAt)}</span>
                    </div>
                    {dream.symbols && dream.symbols.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1">
                          {dream.symbols.map((symbol: any, index: number) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {symbol.name || symbol.symbol || symbol}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setSelectedDream(dream)}
                    className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                  >
                    Detayları Görüntüle
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {dreams.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">Rüya bulunamadı</p>
        </div>
      )}

      {/* Dream Detail Modal */}
      {selectedDream && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedDream.title}
                </h3>
                <button
                  onClick={() => setSelectedDream(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Kapat</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Kullanıcı:</strong> {selectedDream.user.name} ({selectedDream.user.email})
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Tarih:</strong> {formatDate(selectedDream.createdAt)}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Durum:</strong> {selectedDream.interpretations && selectedDream.interpretations.length > 0 ? 'Yorumlanmış' : 'Beklemede'}
                </p>
              </div>
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Rüya İçeriği:</h4>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {selectedDream.content}
                </p>
              </div>
              {selectedDream.symbols && selectedDream.symbols.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Çıkarılan Semboller:</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedDream.symbols.map((symbol: any, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {symbol.name || symbol.symbol || symbol}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DreamsPage;
